import React, { createContext, useContext, useEffect, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import {
  repeatEffect,
  settingsConfig,
  toFormatedDateMMDDHHmm,
} from "utils/commonFunc";
import ApiService from "services/ApiService";
import { defaultChartProps } from "pages/components/CustomDashboardChart";

const TrafficTotalContext = createContext(null);
const colors = ["var(--pr-50)", "var(--alert-50)"];

export function SwItsCrsrdControlDelay({ children }) {
  const [currentData, setCurrentData] = useState();

  useEffect(() => {
    return repeatEffect(async () => {
      const response = await ApiService.get(
        "newdashboard/integrated/GetHourLos"
      );
      if (response.status === 200) {
        const data = response.data;

        const today = { id: "금일", data: [] };
        const range = { id: "월 평균", data: [] };
        const aggregatedData = {};

        data.rows.forEach((p) => {
          if (!aggregatedData[p.hour]) {
            aggregatedData[p.hour] = { todayLos: 0, rangeLos: 0 };
          }
          aggregatedData[p.hour].todayLos = p.todayLos;
          aggregatedData[p.hour].rangeLos = p.rangeLos;
        });

        Object.keys(aggregatedData).forEach((hour) => {
          today.data.push({ x: hour, y: aggregatedData[hour].todayLos });
          range.data.push({ x: hour, y: aggregatedData[hour].rangeLos });
        });

        setCurrentData({
          chartData: [today, range],
          updateTime: data.updateTime,
          unit: "초/대/시",
          todayLabel: `금일 평균 지체시간`,
          todayValue: data.todayValue.toFixed(1), // 소수점 1자리까지 표시
          changeLabel: "월 평균",
          changeValue: `${data.diffPct.toFixed(1)}`, // 소수점 1자리까지
        });
      }
    }, settingsConfig().contentUpdateDelaySec);
  }, []);

  return (
    <TrafficTotalContext.Provider value={currentData}>
      {children}
    </TrafficTotalContext.Provider>
  );
}

export function SwItsCrsrdControlDelayTotalInfo() {
  const currentData = useContext(TrafficTotalContext);
  if (!currentData) return null;

  const changeValueClass =
    parseFloat(currentData.changeValue) >= 0 ? "plus" : "minus";

  // updateTime에서 시간만 받아오기
  const date = new Date(currentData.updateTime);
  let hours = date.getHours() - 1;
  if (hours < 0) hours = 23;
  hours = String(hours).padStart(2, "0");

  return (
    <div className="traffic-info flx flx_col a_center j_center">
      <div className="flx a_flx_end j_center">
        <div className="crntTime">
          <div>금일</div>
          <div>{hours}시</div>
        </div>
        <span className="nbr">{currentData.todayValue}</span>
        <span className="subTxt">{currentData.unit}</span>
      </div>
      <div className="flx a_center j_center">
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {currentData.changeLabel}
        </span>
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {hours}시 평균지체
        </span>
        <span
          className={`cmpr ${changeValueClass}`}
          style={{ fontWeight: "var(--ft-Bold)" }}
        >
          {parseFloat(currentData.changeValue) >= 0 ? "+ " : "- "}
          {Math.abs(currentData.changeValue)}
        </span>
        <span className="cmpr" style={{ marginLeft: "10px" }}>
          %
        </span>
      </div>
    </div>
  );
}

export function SwItsCrsrdControlDelayTotalChart() {
  const currentData = useContext(TrafficTotalContext);
  if (!currentData) return null;

  // 데이터에서 Y값 추출
  const allYValues = currentData.chartData.flatMap((series) =>
    series.data.map((item) => item.y)
  );
  const maxY = Math.max(...allYValues);
  const minY = Math.min(...allYValues.filter((v) => v != null));

  // Y축 눈금 생성 (5단계)
  const generateYTicks = () => {
    const step = (maxY - minY) / 4;
    return [minY, minY + step, minY + step * 2, minY + step * 3, maxY].map(
      (val) => Math.round(val)
    );
  };

  return (
    <div className="traffic-chart" style={{ width: "100%", height: "100%" }}>
      <ResponsiveLine
        data={currentData.chartData.reverse()}
        colors={colors}
        margin={{ top: 10, right: 10, bottom: 40, left: 70 }}
        lineWidth={4}
        xScale={{ type: "point" }}
         yScale={{
           type: "linear",
           min: Math.max(0, minY - 0.5),
           max: maxY + 0.5 ,
         }}
        axisLeft={{

          tickValues: [minY, maxY],
          format: (value) => `${value}`,
          tickSize: 5,
          tickPadding: 10,
          tickColor: "#fff", // 축 눈금 색상 흰색
          legendColor: "#fff", // 축 레전드 색상 흰색
        }}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            translateY: 26,
            itemDirection: "left-to-right",
            itemWidth: 120,
            itemHeight: 0,
            itemTextColor: "#FFFFFF",
            symbolSize: 25,
            fontSize: "var(--ft-26)",
          },
        ]}
        axisBottom={null} 
        gridXValues={[]} // x축 그리드 비활성화 (중요!)
        gridYValues={generateYTicks()} // y축 그리드는 유지
        theme={{
          grid: {
            line: {
              stroke: "#fff", // 그리드 라인 색상 흰색
              strokeWidth: 1.0,
              strokeOpacity: 0.2,
            },
          },
          axis: {
            ticks: {
              text: {
                fill: "#fff", // 축 텍스트 색상 흰색
                fontSize: "var(--ft-26)",
                fontWeight: "var(--ft-Light)",
              },
            },
            legend: {
              text: {
                fill: "#fff", // 축 레전드 텍스트 흰색
                fontSize: "var(--ft-26)",
                fontWeight: "var(--ft-Light)",
              },
            },
          },
          legends: {
            text: {
              fontSize: "var(--ft-26)",
              fontWeight: "var(--ft-Light)",
            },
          },
        }}
        enableArea={true}
        areaBaselineValue={Math.max(0, minY - 0.5)}
        areaOpacity={0.05}
        enablePoints={false}
        useMesh={true}
        tooltip={({ point }) => (
          <div
            style={{
              background: "#333",
              padding: "12px",
              color: "white",
              borderRadius: "4px",
            }}
          >
            <div>{point.data.x}시 평균 지체시간</div>
            <div>
              <strong>{Math.trunc(point.data.y * 10) / 10}초</strong>
            </div>
          </div>
        )}
      />
    </div>
  );
}
